/*
  RetroVerse conditional cart upsells - added Sep 2026.
  Replaces the rules layer UpCart was being kept for.

  Intended behaviour:
    Empty cart           -> no upsells at all, just the empty state
    Console Only in cart -> Hardshell Case, Glass Screen Protector, Game Vault, Silicone
    Complete Bundle      -> Silicone Case only
                            (it already includes a case, screen protector, grip,
                             wifi adapter and the Game Vault card)
    2-Player Bundle      -> Silicone Case + Game Vault card
                            (confirmed Sep 13 2026: the 2-Player Bundle does NOT ship
                             a Game Vault card, so that upsell must stay visible)

  Rules, applied in order:
    0. Empty cart hides everything.
    1. Hide any upsell whose product is already in the cart.
    2. Hide upsells that are already contents of the bundle in the cart.
    3. Optional cap on how many show at once. MAX_VISIBLE = 0 means no cap, which is
       the current setting so the editor config decides, not this file.

  WHY THIS NO LONGER WAITS FOR /cart.js - rewritten Sep 14 2026:

    The drawer re-renders from the server on every cart change - add, remove,
    quantity, the Shipping Protection toggle - and the server prints every
    upsell block that is not literally in the cart. The first version of this
    file waited 250ms after each re-render, fetched /cart.js, and only then hid
    what did not belong. Measured with the Complete Bundle in the cart: the Case
    and Game Vault upsells sat on screen for about 730ms after every render and
    then vanished. That is the flash.

    Everything the rules need is already in the freshly rendered HTML: every
    cart row links to its product, and that link carries the handle. So the
    rules now run inside a MutationObserver callback, which the browser runs
    after the new content is in the DOM but before it paints it. The wrong
    upsells are hidden before they are ever drawn. No delay, no request.

    /cart.js is only a fallback, used when a row exists but its product link
    cannot be read.

  EMPTY CART on the /cart PAGE (Sep 14 2026): the theme re-renders only the cart
  items section after a removal, and the upsell region lives outside it, so it
  persists. The cart items node is observed too, and rule 0 hides the upsells.

  Rows hidden by the instant-remove fix in rv-ui-fixes.js are treated as gone,
  so removing the bundle brings its blocked upsells back on the same frame
  rather than after the server round trip.

  Matching is by substring, not exact handle, deliberately: there are several
  near-identical bundle products in this store (complete-bundle-pack,
  complete-bundle-pack-1, ...-3-0 at $129) and the handles carry a non-ASCII trademark
  character. Substrings survive both.

  CART IMAGES - Sep 14 2026:

    Measured on a phone: adding the Silicone Case from its upsell card left the
    new cart row without a picture for about 1.6s. The row asks for a 300px
    image, the card shows a 150px one, so nothing was cached, and the request
    could not even start until the add had come back from the server. A size
    no browser had requested before took 1.2-1.5s from Shopify's image CDN.

    Two fixes here:
    - Every visible upsell's cart-row image (the selected colour's own photo,
      or the product photo, at CART_IMG_WIDTH) is downloaded in the background
      as soon as the upsell is on screen and again whenever its colour changes.
      By the time Add is tapped the picture is already in the browser cache.
    - Cart row and upsell images are switched from lazy to eager. The drawer
      sits off screen until it opens, so lazy images did not start downloading
      until it slid in, and then popped in one by one.

    The picker on the product page does the same for the product being bought
    (rv-bundle-picker.liquid).

  Safe by design: if the cart cannot be read, nothing is hidden. Worst case you get the
  un-filtered behaviour.
*/
(function () {
  'use strict';

  var MAX_VISIBLE = 0;   /* 0 = no cap */
  var PROTECTION = /shipping[-_ ]?protection/i;

  /* Must match the width the drawer prints for a cart row image
     (image_url: width: 300), or the preloaded copy is a different file. */
  var CART_IMG_WIDTH = 300;

  var BUNDLE_CONTENTS = [
    {
      match: /2-player-bundle/i,
      label: '2-Player Bundle',
      includes: ['protective-case', 'tempered-glass', 'progrip', 'ergonomic', 'wifi-dongle']
    },
    {
      match: /complete-bundle/i,
      label: 'Complete Bundle',
      includes: ['protective-case', 'tempered-glass', 'progrip', 'ergonomic', 'wifi-dongle', 'game-vault']
    },
    {
      match: /^retroversepro$/i,
      label: 'Console Only',
      includes: []
    }
  ];

  function productOf(container) {
    var el = container.querySelector('script[id^="ProductJson-"]');
    if (!el) return null;
    try { return JSON.parse(el.textContent) || null; }
    catch (e) { return null; }
  }

  function handleOf(container) {
    var p = productOf(container);
    return p && p.handle ? p.handle : null;
  }

  function rowHandle(row) {
    var a = row.querySelector('a[href*="/products/"]');
    if (!a) return null;
    var m = (a.getAttribute('href') || '').match(/\/products\/([^?#\/]+)/);
    if (!m) return null;
    try { return decodeURIComponent(m[1]).toLowerCase(); }
    catch (e) { return m[1].toLowerCase(); }
  }

  /* Product handles in the cart, read from the rows on screen. Returns null if
     any row's handle cannot be read, which sends the caller to the fallback. */
  function handlesFromRows(rows) {
    var out = [];
    for (var i = 0; i < rows.length; i++) {
      if (rows[i].style.display === 'none') continue;
      var h = rowHandle(rows[i]);
      if (!h) return null;
      if (PROTECTION.test(h)) continue;
      out.push(h);
    }
    return out;
  }

  function show(container, visible) {
    if (visible) {
      if (container.getAttribute('data-rv-hidden') === '1') {
        container.style.removeProperty('display');
        container.removeAttribute('data-rv-hidden');
      }
    } else if (container.getAttribute('data-rv-hidden') !== '1') {
      container.style.setProperty('display', 'none', 'important');
      container.setAttribute('data-rv-hidden', '1');
    }
  }

  function eager(nodes) {
    for (var i = 0; i < nodes.length; i++) {
      var imgs = nodes[i].querySelectorAll('img[loading="lazy"]');
      for (var k = 0; k < imgs.length; k++) imgs[k].loading = 'eager';
    }
  }

  /* ---- background download of each upsell's cart-row image ---- */
  var warmed = {};

  function warm(url) {
    if (!url || warmed[url]) return;
    warmed[url] = true;
    var img = new Image();
    img.decoding = 'async';
    img.src = url;
  }

  function cartImageFor(container) {
    var p = productOf(container);
    if (!p) return null;
    var input = container.querySelector('form input[name="id"]');
    var id = input ? parseInt(input.value, 10) : 0;
    var src = null;
    if (id && p.variants) {
      for (var i = 0; i < p.variants.length; i++) {
        var v = p.variants[i];
        if (v.id === id && v.featured_image && v.featured_image.src) { src = v.featured_image.src; break; }
      }
    }
    if (!src) src = p.featured_image;
    if (!src) return null;
    return src + (src.indexOf('?') > -1 ? '&' : '?') + 'width=' + CART_IMG_WIDTH;
  }

  function warmUpsells(containers) {
    for (var i = 0; i < containers.length; i++) {
      if (containers[i].getAttribute('data-rv-hidden') === '1') continue;
      warm(cartImageFor(containers[i]));
    }
  }

  function apply(containers, inCart, attrRoot) {
    /* ---- rule 0: empty cart shows no upsells ---- */
    if (!inCart.length) {
      for (var e = 0; e < containers.length; e++) show(containers[e], false);
      attrRoot.setAttribute('data-rv-bundle', 'empty');
      attrRoot.setAttribute('data-rv-upsells-shown', '0');
      return;
    }

    var blocked = [];
    var bundleLabel = null;

    for (var i = 0; i < inCart.length; i++) {
      for (var r = 0; r < BUNDLE_CONTENTS.length; r++) {
        if (BUNDLE_CONTENTS[r].match.test(inCart[i])) {
          bundleLabel = BUNDLE_CONTENTS[r].label;
          blocked = blocked.concat(BUNDLE_CONTENTS[r].includes);
          break;
        }
      }
    }

    var shown = 0;

    for (var k = 0; k < containers.length; k++) {
      var el = containers[k];
      var handle = (handleOf(el) || '').toLowerCase();
      var visible = true;

      if (!handle) {
        visible = true;
      } else if (inCart.indexOf(handle) > -1) {
        visible = false;
      } else {
        for (var b = 0; b < blocked.length; b++) {
          if (handle.indexOf(blocked[b]) > -1) { visible = false; break; }
        }
      }

      if (visible && MAX_VISIBLE > 0 && shown >= MAX_VISIBLE) visible = false;
      if (visible) shown++;

      show(el, visible);
    }

    attrRoot.setAttribute('data-rv-bundle', bundleLabel || 'none');
    attrRoot.setAttribute('data-rv-upsells-shown', String(shown));
    warmUpsells(containers);
  }

  function applyFromCartJs(containers, attrRoot) {
    fetch('/cart.js', { credentials: 'same-origin' })
      .then(function (r) { return r.json(); })
      .then(function (c) {
        if (!c || !c.items) return;
        var hs = [];
        for (var i = 0; i < c.items.length; i++) {
          var h = (c.items[i].handle || '').toLowerCase();
          if (h && !PROTECTION.test(h)) hs.push(h);
        }
        apply(containers, hs, attrRoot);
      })
      .catch(function () {});
  }

  function applyTo(containers, rows, attrRoot) {
    if (!containers.length) return;
    eager(containers);
    var inCart = handlesFromRows(rows);
    if (inCart) apply(containers, inCart, attrRoot);
    else applyFromCartJs(containers, attrRoot);
  }

  function run() {
    var d = document.querySelector('cart-drawer');
    if (d) {
      var rows = d.querySelectorAll('cart-drawer-items .cart-item');
      eager(rows);
      applyTo(
        d.querySelectorAll('.upsell-product-container'),
        rows,
        d.querySelector('.cart-drawer') || d
      );
    }

    /* The /cart page renders its own upsells outside the items section. */
    var page = document.querySelector('#main-cart-items');
    if (page) {
      var all = document.querySelectorAll('.upsell-product-container');
      var outside = [];
      for (var i = 0; i < all.length; i++) {
        if (!all[i].closest('cart-drawer')) outside.push(all[i]);
      }
      applyTo(outside, page.querySelectorAll('.cart-item'), document.body);
    }
  }

  function start() {
    run();

    if (window.MutationObserver) {
      /* Synchronous on purpose. See the note at the top: this callback runs
         before the browser paints the re-rendered drawer. Hiding an upsell is
         an attribute change, which this observer does not watch, so it cannot
         trigger itself. */
      var mo = new MutationObserver(run);
      var d = document.querySelector('cart-drawer');
      if (d) mo.observe(d, { childList: true, subtree: true });
      var page = document.querySelector('#main-cart-items');
      if (page) mo.observe(page, { childList: true, subtree: true });
    }

    /* The instant-remove fix hides the row with a style change, which the
       observer does not see. Re-run once every click handler has had its turn. */
    document.addEventListener('click', function (e) {
      var t = e.target;
      if (t && t.closest && t.closest('.cart-remove-button, cart-remove-button')) {
        setTimeout(run, 0);
      }
    }, true);

    /* rv-cart-protection-cleanup.js hides rows that are waiting to be removed.
       Re-run straight away so the upsells match what is on screen. */
    document.addEventListener('rv:cart-rows-changed', run);

    /* A colour change on an upsell switches which photo its cart row will use.
       The theme updates the card's variant input in its own change handler, so
       read it once that has run. */
    document.addEventListener('change', function (e) {
      var t = e.target;
      var card = t && t.closest ? t.closest('.upsell-product-container') : null;
      if (card) setTimeout(function () { warmUpsells([card]); }, 0);
    }, true);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', start);
  } else {
    start();
  }
})();
/* Oct 9 2026 - RetroVerse Stick cart upsells. Runs after the rules above.
   - Stick in the cart with no R36S: show the R36S console upsell and hide the
     R36S accessories (they don't fit the Stick).
   - Stick Controllers (2-Pack) upsell: only when a Stick is in the cart.
   - The R36S console upsell shows only in the first case.
   "Stick in the cart" also counts the Stick Controllers, whose handle starts
   with retroverse-stick. */
(function () {
  var ACCESSORIES = /game-vault|silicone-case|protective-case|tempered-glass|progrip|ergonomic|joystick-caps/i;
  var STICK = /retroverse-stick/i;
  var STICK_ADDONS = /retroverse-stick-controllers/i;
  var R36S = /^retroversepro$|complete-bundle|2-player-bundle/i;
  var CONSOLE = /^retroversepro$/i;

  function stickRule() {
    var d = document.querySelector('cart-drawer');
    if (!d) return;
    var stick = false, r36s = false;
    d.querySelectorAll('cart-drawer-items .cart-item').forEach(function (row) {
      var a = row.style.display !== 'none' && row.querySelector('a[href*="/products/"]');
      var h = a ? ((a.getAttribute('href') || '').match(/\/products\/([^?#\/]+)/) || [])[1] || '' : '';
      if (STICK.test(h)) stick = true;
      if (R36S.test(h)) r36s = true;
    });
    var stickOnly = stick && !r36s;
    d.querySelectorAll('.upsell-product-container').forEach(function (card) {
      var h = '';
      try { h = JSON.parse(card.querySelector('script[id^="ProductJson-"]').textContent).handle || ''; } catch (e) {}
      var hide;
      if (CONSOLE.test(h)) hide = !stickOnly;
      else if (STICK_ADDONS.test(h)) hide = !stick;
      else hide = stickOnly && ACCESSORIES.test(h);
      if (hide) {
        card.setAttribute('data-rv-stick', '1');
        card.style.setProperty('display', 'none', 'important');
      } else if (card.getAttribute('data-rv-stick') === '1') {
        card.removeAttribute('data-rv-stick');
        if (card.getAttribute('data-rv-hidden') !== '1') card.style.removeProperty('display');
      }
    });
  }

  function start() {
    stickRule();
    var d = document.querySelector('cart-drawer');
    if (d && window.MutationObserver) new MutationObserver(stickRule).observe(d, { childList: true, subtree: true });
    document.addEventListener('rv:cart-rows-changed', stickRule);
    document.addEventListener('click', function (e) {
      if (e.target.closest && e.target.closest('.cart-remove-button, cart-remove-button')) setTimeout(stickRule, 0);
    }, true);
  }

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', start);
  else start();
})();
